import { useCallback, useMemo, useState } from "react";

import { useObjectMemo } from "../../../hooks";
import { useAccounts } from "../accounts";

import type {
  ActiveFilters,
  DocumentFilterTypes,
  Document,
  FilterChoices,
} from "./types";

export const getYearForDocument = (document: Document) =>
  document.created_at.substring(0, 4);

const getValuesAsArray = (value: string | string[]): string[] =>
  Array.isArray(value) ? value : [value];

export const isFilterActive = (
  activeFilters: ActiveFilters,
  filterType: DocumentFilterTypes,
  value: string | string[],
) => {
  const values = getValuesAsArray(value);
  return values.every((v) => activeFilters[filterType]?.has(v));
};

export const getFilteredDocuments = <T extends Document>(
  activeFilters: ActiveFilters,
  documents: T[],
) => {
  const activeYearFilters = activeFilters.years;
  const activeTypeFilters = activeFilters.typeLabels;
  return documents.filter((d) => {
    const hasYear =
      activeYearFilters.size > 0
        ? activeYearFilters.has(getYearForDocument(d))
        : true;
    const hasType =
      activeTypeFilters.size > 0 ? activeTypeFilters.has(d.type) : true;
    const activeAccountFilters = activeFilters.accounts;
    const hasAccount =
      activeAccountFilters.size > 0
        ? activeAccountFilters.has(d.account_id)
        : true;

    return hasYear && hasType && hasAccount;
  });
};

export const computeNewActiveFilters = (
  activeFilters: Record<DocumentFilterTypes, Set<string>>,
  filterType: DocumentFilterTypes,
  value: string | string[],
) => {
  const values = getValuesAsArray(value);
  const newActiveFilters = new Set(activeFilters[filterType]);
  // If all values are currently active, remove them all; otherwise add them all
  const allActive = values.every((v) => newActiveFilters.has(v));
  if (allActive) {
    values.forEach((v) => newActiveFilters.delete(v));
  } else {
    values.forEach((v) => newActiveFilters.add(v));
  }

  return { ...activeFilters, [filterType]: newActiveFilters };
};

export const useDocumentFilters = <T extends Document>(documents: T[]) => {
  const accounts = useAccounts();

  const filterChoices: FilterChoices = useMemo(() => {
    const filters = {
      years: new Set<string>(),
      typeLabels: {} as Record<string, Set<string>>,
      accounts: {} as Record<string, string>,
    };
    documents.forEach((d) => {
      const documentAccount = accounts.find(
        (account) => account.id === d.account_id,
      );
      filters.years.add(getYearForDocument(d));
      filters.typeLabels[d.type_label] =
        filters.typeLabels[d.type_label] ?? new Set<string>();
      filters.typeLabels[d.type_label].add(d.type);
      filters.accounts[d.account_id] = documentAccount
        ? documentAccount.getName()
        : d.description;
    });

    // Convert typeLabels Sets to arrays
    const typeLabelsAsArrays = Object.entries(filters.typeLabels).reduce(
      (acc, [label, types]) => {
        acc[label] = [...types];
        return acc;
      },
      {} as Record<string, string[]>,
    );
    return {
      years: [...filters.years],
      typeLabels: typeLabelsAsArrays,
      accounts: filters.accounts,
    };
  }, [documents, accounts]);

  const [activeFilters, setActiveFilters] = useState<
    Record<DocumentFilterTypes, Set<string>>
  >({
    years: new Set([]),
    typeLabels: new Set([]),
    accounts: new Set([]),
  });

  const filteredDocuments = useMemo(
    () => getFilteredDocuments(activeFilters, documents),
    [activeFilters, documents],
  );

  const toggleActiveFilters = useCallback(
    (filterType: DocumentFilterTypes, value: string | string[]) => {
      setActiveFilters((prevFilters) =>
        computeNewActiveFilters(prevFilters, filterType, value),
      );
    },
    [],
  );

  const isFilterActiveFn = useCallback(
    (filterType: DocumentFilterTypes, filterValue: string | string[]) =>
      isFilterActive(activeFilters, filterType, filterValue),
    [activeFilters],
  );

  const clearFilters = useCallback(() => {
    setActiveFilters({
      years: new Set(),
      typeLabels: new Set(),
      accounts: new Set(),
    });
  }, []);

  return useObjectMemo({
    filteredDocuments,
    setActiveFilters,
    toggleActiveFilters,
    clearFilters,
    isFilterActive: isFilterActiveFn,
    filterChoices,
    activeFilters,
  });
};
